<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
            class="daily-inquiries-report"
            @first-data-rendered="headerHeightSetter('firstDataRendered')"
            @column-resized="headerHeightSetter('columnResized')"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <div class="d-flex align-items-baseline mr-1 mb-1 filter-properties">
                <form-search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <responsive-button
                :href="exportExcelHref"
                :icon="['fas', 'file-excel']"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            />
             <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
                :excelOnly="true"
            />
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import API from '../../api';
    import Page from '../generic/page/page.vue';
    import Grid from '../generic/grid/grid.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';
    import DateRangePicker, {
        nameToRange,
        TODAY,
        YESTERDAY,
        THIS_MONTH,
        LAST_MONTH,
        ALL_DATES,
        TOMORROW,
        THIS_WEEK,
        NEXT_WEEK,
        NEXT_MONTH,
        THIS_YEAR,
        LAST_WEEK,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import { REPORT_DAILY_INQUIRIES_ENDPOINT, REPORT_PROPERTIES_ALL } from '../../endpoints';
    import ReportSummaryBar from './report-summary-bar.vue';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
    import ReportSettingsModal, {
        MULTI_SELECT_INPUT,
        DATE_PERIODS_INPUT,
    } from './report-settings/report-settings-modal.vue';

    export default {
        name: 'ReportDailyInquiries',
        components: {
            Page,
            Grid,
            ResponsiveButton,
            DateRangePicker,
            FormSearchMultiselect,
            reportSummaryBar: ReportSummaryBar,
            ReportSettingsModal
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                title: 'Daily Inquiries',
                tabs: [
                    {
                        title: 'Daily Inquiries',
                        route: '/app/reports/daily-inquiries/',
                        active: true,
                    },
                ],
                dateRange: nameToRange(THIS_YEAR),
                predefinedRanges: [
                    YESTERDAY,
                    TODAY,
                    TOMORROW,
                    LAST_WEEK,
                    THIS_WEEK,
                    NEXT_WEEK,
                    LAST_MONTH,
                    THIS_MONTH,
                    NEXT_MONTH,
                    THIS_YEAR,
                    ALL_DATES,
                ],
                dateRequestFormat: 'yyyy-MM-dd',
                busy: false,
                gridApi: null,
                rowData: [],
                columnDefs: [],
                getRowStyle: null,
                pinnedBottomRowData: [],
                windowWidth: 0,
                windowHeight: 0,
                filterProperties: [],
                filterPropertyOptions: [],
                statusBar: null,
                reportSettingsModalId: 'report-settings-modal',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            exportExcelHref() {
                let url = '/app/reports/daily-inquiries/row-data/excel/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&properties=${this.filterProperties.join(',')}`;
                return url;
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            isMobile() {
                const md = 768;
                return this.windowWidth < md || this.windowHeight < md;
            },
            reportSettings() {
                return {
                    name: 'Daily Inquires',
                    report_sysname: 'DAILY_INQUIRIES',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            predefinedRanges: this.predefinedRanges,
                            value: 'this_month',
                            label: 'Date Period',
                            name: 'from_date',
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            value: [],
                            label: 'Only for Properties',
                            name: 'properties',
                            options: this.filterPropertyOptions,
                            placeholder: 'All Properties'
                        },
                    ],
                };
        },
        },
        mounted() {
            this.fetchProperties();
            this.fetchData();
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        beforeUnmount() { 
            window.removeEventListener('resize', this.onResize);
        },
        beforeMount() {
            this.getRowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
            this.statusBar = {
                statusPanels: [
                    {
                        key: 'reportSummaryBar',
                        statusPanel: 'reportSummaryBar',
                        align: 'left',
                    },
                ],
            };
        },
        methods: {
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(
                        REPORT_DAILY_INQUIRIES_ENDPOINT(),
                        {
                            params: {
                                from_date: this.startDateRequest,
                                to_date: this.endDateRequest,
                                properties: this.filterProperties.join(',')
                            },
                        }
                    );
                    const { inquiries, total_inquiries, all_total } = data;
                    // get rowData
                    this.rowData = [...inquiries.map((inquiry, index) => ({
                        id: index,
                        date: inquiry.date,
                        ...inquiry.sources,
                        total: inquiry.total,
                    }))]
                    // get column definition
                    this.columnDefs = [
                        {
                            field: 'date',
                            headerName: 'Date',
                            type: 'dateColumn',
                            minWidth: 110,
                        },
                        ...Object.keys(total_inquiries).map((key, index) => {
                            const { name } = total_inquiries[key]
                            return {
                                field: key,
                                headerName: name,
                            };
                        }),
                        {
                            field: 'total',
                            headerName: 'Total',
                            minWidth: 80,
                            autoWidthExclude: true
                        },
                    ];
                    // get data for pinned bottom field
                    const statusBar = {};
                    this.columnDefs.slice(1).forEach((column) => {
                        statusBar[column.field] = column.field === 'total' ? all_total : total_inquiries[column.field].total;
                    });
                    this.pinnedBottomRowData = [{ ...statusBar }];
                } catch (e) {
                    this.errorToast({ text: "Could not fetch data" });
                } finally {
                    this.busy = false
                }
            },
            async fetchProperties() {
                const { data } = await API.get('/app/reports/daily-inquiries/row-data/properties/');
                this.filterPropertyOptions = data
                    .map((item) => ({ value: item.property_id, text: item.name, disabled: item.disabled }))
            },
            onResize() {
                this.windowWidth = window.innerWidth;
                this.windowHeight = window.innerHeight;
                const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
                let componentInstance = statusBarComponent;
                if (statusBarComponent.getFrameworkComponentInstance) {
                    componentInstance = statusBarComponent.getFrameworkComponentInstance();
                }
                componentInstance.setVisible(this.isMobile);
                componentInstance.setTitle(this.title);
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.fetchData();
            },
            headerHeightGetter() {
                const columnHeaderTexts = [
                    ...document.querySelectorAll('.ag-header-cell-text'),
                ];
                const clientHeights = columnHeaderTexts.map(
                    headerText => headerText.clientHeight
                );
                const tallestHeaderTextHeight = Math.max(...clientHeights);

                return tallestHeaderTextHeight;
            },
            headerHeightSetter(eventString) {
                const padding = 20;
                const height = this.headerHeightGetter() + padding;
                const grid = this.$refs.grid;
                const originHeight = grid.gridOptions.headerHeight;
                if (height > 0 && originHeight > 0 && height !== originHeight) {
                    this.gridApi.setHeaderHeight(height);
                    this.gridApi.resetRowHeights();
                }
            },
        },
        watch: {
            filterProperties: {
                deep: true,
                handler() {
                    this.fetchData();
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
.filter-properties {
    > fieldset {
        margin: 0 !important;
    }
}
</style>